import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { bugsAPI, changeRequestsAPI, clientsAPI, projectRolesAPI, projectStagesAPI, projectTypesAPI, projectsAPI, tasksAPI, trainingAPI, usersAPI } from '../utils/api';
import { useProjectPermissions } from '../hooks/usePermissions';
import { PROJECT_TYPES } from './ProjectsPage';
import { Badge } from '../components/layout/UI';
import { format, parseISO, isValid } from 'date-fns';

import { useToast, apiError, useConfirm } from '../components/Toast';
import CustomFieldsEditor from '../components/CustomFieldsEditor';
import { useEditableRecord, SaveBar } from '../components/UnsavedChanges';
import { useMaster } from '../hooks/useMasters';
import { ArrowLeft, ChevronRight, Lock, Plus } from 'lucide-react';
import { WORK_STATUSES } from '../utils/workStatus';

// ── Icons (only those actually used) ──────────────────────────────────────
const PlusIc = () => <Plus size={14} />;
const LockIc = () => <Lock size={12} />;

/**
 * Fallbacks only. The real lists come from Masters, so an admin can change
 * them. These are copied from ProjectsPage exactly as they are there, and used
 * only when Masters is empty or unreachable.
 */
const STATUS_FALLBACK     = ['Not Started','Active','On Hold','Completed'].map(v=>({value:v,label:v}));
const PRIORITY_FALLBACK   = ['Low','Medium','High','Critical'].map(v=>({value:v,label:v}));
const ONBOARDING_FALLBACK = ['Kickoff','Requirements','Configuration','Testing','Training','Go Live','Hypercare']
  .map(v=>({value:v,label:v}));
const MATURITY_FALLBACK   = ['Business as Usual','Present and Active','Formalized','Strategic','Converged','Innovative']
  .map((v,i)=>({value:v,label:`#${i+1} - ${v}`}));

const TABS = [
  { key:'overview', label:'Overview'        },
  { key:'details',  label:'Details'         },
  { key:'tasks',    label:'Tasks'           },
  { key:'bugs',     label:'Bugs'            },
  { key:'changes',  label:'Change Requests' },
  { key:'training', label:'Training'        },
  { key:'team',     label:'Team & Roles'    },
  { key:'stages',   label:'Workflow'        },
];

const fmt = (val, pattern = 'MMM d, yyyy') => {
  if (!val) return '—';
  try {
    const d = typeof val === 'string' ? parseISO(val) : new Date(val);
    return isValid(d) ? format(d, pattern) : '—';
  } catch { return '—'; }
};

const fmtMoney = (v, currency = 'USD') => v
  ? new Intl.NumberFormat('en-US', { style:'currency', currency, maximumFractionDigits:0 }).format(v)
  : '—';

// ── RAG indicator ──────────────────────────────────────────────────────────
const RAG_MAP = {
  G: { label:'On Track',  bg:'#D1FAE5', color:'#065f46', dot:'#10b981' },
  Y: { label:'At Risk',   bg:'#FEF3C7', color:'#92400E', dot:'#FFC849' },
  R: { label:'Off Track', bg:'#FEE2E2', color:'#991B1B', dot:'#ef4444' },
};

const RagBadge = ({ value, label, editable, onChange }) => {
  const s = RAG_MAP[value] || { label:'Not set', bg:'var(--surface-alt)', color:'var(--text-secondary)', dot:'#94a3b8' };
  if (!editable) return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: s.bg }}>
      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.dot }}/>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: s.color }}>{label}</p>
        <p className="text-xs font-medium" style={{ color: s.color }}>{s.label}</p>
      </div>
    </div>
  );
  return (
    <div className="rounded-xl overflow-hidden border" style={{ borderColor:'var(--border)' }}>
      <p className="text-xs font-semibold uppercase tracking-wider px-3 pt-2 pb-1" style={{ color:'var(--text-secondary)' }}>{label}</p>
      <div className="flex">
        {['G','Y','R'].map(v => (
          <button key={v} onClick={() => onChange(v)}
            className="flex-1 py-1.5 text-xs font-bold transition-colors"
            style={{
              background: value === v ? RAG_MAP[v].bg : 'transparent',
              color: value === v ? RAG_MAP[v].color : 'var(--text-secondary)',
            }}>
            {v === 'G' ? 'Green' : v === 'Y' ? 'Amber' : 'Red'}
          </button>
        ))}
      </div>
    </div>
  );
}

const StatBox = ({ label, value, color, warning }) => (
  <div className="card px-4 py-3 text-center">
    <p className="text-2xl font-bold" style={{ color: color || 'var(--text)' }}>{value}</p>
    {warning && <span className="inline-block w-2 h-2 rounded-full bg-red-500 ml-1 align-middle"/>}
    <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>{label}</p>
  </div>
);

const ENV_STYLE = {
  Development: 'bg-blue-50 text-blue-700 border border-blue-200',
  UAT:         'bg-amber-50 text-amber-700 border border-amber-200',
  Production:  'bg-green-50 text-green-700 border border-green-200',
  Staging:     'bg-purple-50 text-purple-700 border border-purple-200',
};

function StageTimeline({ stages, currentStage }) {
  const currentIdx = stages.findIndex(s => s.stage_name === currentStage);
  return (
    <div className="flex items-start overflow-x-auto pb-2">
      {stages.map((stage, i) => {
        const isCurrent = stage.stage_name === currentStage;
        const isPast    = currentIdx > -1 && i < currentIdx;
        return (
          <div key={i} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ background:isCurrent?'#2E539F':isPast?'#10b981':'var(--surface-alt)', color:isCurrent||isPast?'white':'var(--text-secondary)' }}>
                {isPast?'v':i+1}
              </div>
              <p className="text-xs text-center mt-1" style={{ color:isCurrent?'#2E539F':'var(--text-secondary)', fontWeight:isCurrent?700:400, maxWidth:72 }}>
                {stage.stage_name}
                {stage.requires_approval&&<span className="block" style={{color:'#FFC849',fontSize:10}}>Gate</span>}
              </p>
            </div>
            {i<stages.length-1&&<div className="flex-1 h-0.5 mx-1 mt-4" style={{background:isPast?'#10b981':'var(--border)'}}/>}
          </div>
        );
      })}
    </div>
  );
}


export default function ProjectDetailPage() {
  const confirm = useConfirm();
  const toast = useToast();
  const { id } = useParams();
  const navigate = useNavigate();

  // Resolve what this user can do on this project
  const { can, loading: permLoading } = useProjectPermissions(id);

  const [project,    setProject]    = useState(null);
  const [error, setError] = useState('');
  const [tasks,      setTasks]      = useState([]);
  const [bugs,       setBugs]       = useState([]);
  const [crs,        setCrs]        = useState([]);
  const [trainings,  setTrainings]  = useState([]);
  const [stages,     setStages]     = useState([]);
  const [stageHistory, setStageHistory] = useState([]);
  const [projectRoles, setProjectRoles] = useState([]);
  const [roleDefs,   setRoleDefs]   = useState([]);
  const [allUsers,   setAllUsers]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [activeTab,  setActiveTab]  = useState('overview');

  // RAG editing state
  const [editingRAG,  setEditingRAG]  = useState(false);
  const [ragValues,   setRagValues]   = useState({ schedule:'', budget:'', scope:'', risk:'' });

  // Advance stage modal
  const [advanceModal, setAdvanceModal] = useState(false);
  // One editable form, no modal. See components/UnsavedChanges.jsx.
  const { form, set, discard, dirtyFields, patch } = useEditableRecord(project);

  // Admin-controlled, with the values the create form uses as the fallback.
  const STATUS_OPTS    = useMaster('project_status',   STATUS_FALLBACK);
  const PRIORITY_OPTS  = useMaster('priority',         PRIORITY_FALLBACK);
  const ONBOARDING     = useMaster('onboarding_stage', ONBOARDING_FALLBACK);
  const MATURITY       = useMaster('maturity_stage',   MATURITY_FALLBACK);
  const [projectTypes, setProjectTypes] = useState([]);
  const [savingProj, setSavingProj] = useState(false);

  /**
   * Sends only what changed. The RAG card saves the four health scores
   * separately, so this never touches them — two controls writing one value is
   * how they end up disagreeing.
   */
  const handleSaveProject = async () => {
    setSavingProj(true);
    try {
      await projectsAPI.update(id, patch());
      toast.success(`Saved ${dirtyFields.length} change${dirtyFields.length === 1 ? '' : 's'}`);
      load();
    } catch (e) {
      toast.error(apiError(e, 'Could not save those changes.'));
    } finally { setSavingProj(false); }
  };

  const [clients, setClients] = useState([]);
  useEffect(() => {
    clientsAPI.getAll({ limit: 0 }).then(r => setClients(r.data || [])).catch(() => setClients([]));
  }, []);
  useEffect(() => {
    projectTypesAPI.getAll()
      .then(r => setProjectTypes((r.data || []).filter(t => t.is_active !== false)))
      .catch(() => setProjectTypes([]));
  }, []);
  const [allClients,  setAllClients]  = useState([]);
  const [editProjSaving,setEditProjSaving]=useState(false);
  const [bugModal2,    setBugModal2]    = useState(false);
  const [crModal2,     setCrModal2]     = useState(false);
  const [qBugForm,     setQBugForm]     = useState({});
  const [qCRForm,      setQCRForm]      = useState({});
  const [qSaving,      setQSaving]      = useState(false);
  const [advanceReason,   setAdvanceReason]   = useState('');
  const [advanceAssignTo, setAdvanceAssignTo] = useState('');
  const [advancing, setAdvancing] = useState(false);
  const [advanceError, setAdvanceError] = useState('');

  const load = useCallback(async () => {
    try {
      const [pR, tR, bR, cR, trR] = await Promise.all([
        projectsAPI.getById(id),
        tasksAPI.getAll({ project_id: id , limit: 0 }),
        bugsAPI.getAll({ project_id: id , limit: 0 }),
        changeRequestsAPI.getAll({ project_id: id , limit: 0 }),
        trainingAPI.getAll({ project_id: id , limit: 0 }),
      ]);
      const p = pR.data;
      setProject(p);
      setTasks(Array.isArray(tR.data) ? tR.data : []);
      setBugs(Array.isArray(bR.data) ? bR.data : []);
      setCrs(Array.isArray(cR.data) ? cR.data : []);
      setTrainings(Array.isArray(trR.data) ? trR.data : []);
      setRagValues({
        schedule: p.health_schedule || '',
        budget:   p.health_budget   || '',
        scope:    p.health_scope    || '',
        risk:     p.health_risk     || '',
      });
      // Load stages for this project type
      if (p.project_type) {
        const [stR, shR, prR, rdR, uR] = await Promise.all([
          projectStagesAPI.getDefinitions(p.project_type),
          projectStagesAPI.getHistory(id),
          projectRolesAPI.getForProject(id),
          projectRolesAPI.getDefinitions(p.project_type),
          usersAPI.getAll(),
        ]);
        setStages(stR.data || []);
        setStageHistory(shR.data || []);
        setProjectRoles(prR.data || []);
        setRoleDefs(rdR.data || []);
        setAllUsers(uR.data || []);
      }
    } catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { import('../utils/api').then(m => m.clientsAPI.getAll({ limit: 0 }).then(r => setAllClients(r.data||[]))); }, []);

  const saveRAG = async () => {
    try {
      await projectsAPI.update(id, {
        ...project,
        health_schedule: ragValues.schedule || null,
        health_budget:   ragValues.budget   || null,
        health_scope:    ragValues.scope    || null,
        health_risk:     ragValues.risk     || null,
      });
      toast.success('Health scores updated');
      setProject(p => ({ ...p, ...ragValues.schedule && { health_schedule: ragValues.schedule }, health_budget: ragValues.budget, health_scope: ragValues.scope, health_risk: ragValues.risk }));
      setEditingRAG(false);
      load();
    } catch(e) { toast.error(apiError(e, 'Failed to save health scores. You may not have permission to edit this project.')); }
  };

  const handleAdvance = async () => {
    if (!advanceReason.trim()) { setAdvanceError('A reason is required'); return; }
    setAdvancing(true); setAdvanceError('');
    try {
      await projectStagesAPI.advance(id, { reason: advanceReason, assigned_to: advanceAssignTo || undefined });
      setAdvanceModal(false); setAdvanceReason(''); setAdvanceAssignTo('');
      load();
    } catch(e) {
      setAdvanceError(e.response?.data?.message || 'Could not advance stage');
    } finally { setAdvancing(false); }
  };

  const assignRole = async (userId, roleId) => {
    try { await projectRolesAPI.assign(id, { user_id: userId, role_id: roleId }); load(); }
    catch(e) { toast.error(apiError(e, 'Could not assign role')); }
  };

  const removeRole = async (assignmentId) => {
    if (!await confirm('Remove this role assignment?')) return;
    try { await projectRolesAPI.remove(assignmentId); load(); }
    catch(e) { toast.error(apiError(e, 'Could not remove role')); }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/>
    </div>
  );

  if (!project) return (
    <div className="card text-center py-16">
      <p style={{ color:'var(--text)' }}>Project not found</p>
      <button onClick={() => navigate('/projects')} className="btn-secondary mt-3">Back to Projects</button>
    </div>
  );

  const progress   = parseInt(project.progress) || 0;
  const openBugs   = bugs.filter(b => !['Closed','Verified'].includes(b.status)).length;
  const critBugs   = bugs.filter(b => b.severity === 'Critical' && !['Closed','Verified'].includes(b.status)).length;
  const doneTasks  = tasks.filter(t => t.status === 'Completed').length;
  const pendingCRs = crs.filter(c => c.approval_status === 'Pending').length;
  const trainingHrs= trainings.reduce((s,t) => s + (t.duration_minutes||0), 0);

  // Budget health
  const budgetUsedPct = project.budget_amount && project.actual_spend
    ? Math.round((project.actual_spend / project.budget_amount) * 100)
    : null;

  // Schedule variance (if baseline locked)
  const scheduleVariance = project.baseline_locked && project.end_date && project.baseline_end_date
    ? Math.round((new Date(project.end_date) - new Date(project.baseline_end_date)) / 86400000)
    : null;

  const currentStageIdx = project.current_stage
    ? stages.findIndex(s => s.stage_name === project.current_stage)
    : -1;
  const isLastStage = currentStageIdx >= stages.length - 1;

  return (
    <div className="space-y-5">

      {/* Breadcrumb */}
      <button onClick={() => navigate('/projects')}
        className="flex items-center gap-1.5 text-sm transition-colors hover:text-brand-500"
        style={{ color:'var(--text-secondary)' }}>
        <ArrowLeft size={16}/> Back to Projects
      </button>

      {/* Header */}
      <div className="card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap mb-2">
              <h1 className="text-xl font-bold" style={{ color:'var(--text)' }}>{project.name}</h1>
              <Badge value={project.status}   type="status"/>
              <Badge value={project.priority} type="priority"/>
              {project.project_type && project.project_type !== 'General' && (
                <span className={`badge border text-xs ${(PROJECT_TYPES[project.project_type] || PROJECT_TYPES['General']).badge}`}>
                  {project.project_type}
                </span>
              )}
              {project.baseline_locked && (
                <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                  style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>
                  <LockIc /> Baseline locked
                </span>
              )}
            </div>
            {project.client && (
              <p className="text-sm" style={{ color:'var(--text-secondary)' }}>
                Client: <span className="font-medium" style={{ color:'var(--text)' }}>{project.client}</span>
              </p>
            )}
            {project.description && (
              <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>{project.description}</p>
            )}
            {/* Key project metadata */}
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-xs" style={{ color:'var(--text-secondary)' }}>
              {project.start_date && <span>Start: <strong style={{ color:'var(--text)' }}>{fmt(project.start_date)}</strong></span>}
              {project.end_date   && <span>End: <strong style={{ color:'var(--text)' }}>{fmt(project.end_date)}</strong></span>}
              {scheduleVariance !== null && (
                <span style={{ color: scheduleVariance > 0 ? '#ef4444' : '#10b981' }}>
                  {scheduleVariance > 0 ? `+${scheduleVariance}d vs baseline` : scheduleVariance === 0 ? 'On schedule' : `${scheduleVariance}d ahead`}
                </span>
              )}
              {project.go_live_date && <span>Go-live: <strong style={{ color:'var(--text)' }}>{fmt(project.go_live_date)}</strong></span>}
              {project.client_contact && <span>Contact: <strong style={{ color:'var(--text)' }}>{project.client_contact}</strong></span>}
              {project.contract_number && <span>Contract: <strong style={{ color:'var(--text)' }}>{project.contract_number}</strong></span>}
            </div>
            {/* Current stage pill */}
            {project.current_stage && (
              <div className="mt-2">
                <span className="text-xs px-2.5 py-1 rounded-full font-medium"
                  style={{ background:'rgba(46,83,159,.12)', color:'#2E539F' }}>
                  Stage: {project.current_stage}
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 flex-shrink-0">
            <div className="flex gap-2 flex-wrap">
              <Link to={`/tasks?project_id=${id}`} className="btn-secondary text-sm flex items-center gap-1.5"><PlusIc/> Task</Link>
              <button onClick={()=>{setQBugForm({project_id:id,severity:'Minor',issue_type:'Bug',title:'',description:''});setBugModal2(true);}}
                className="btn-secondary text-sm flex items-center gap-1.5"><PlusIc/> Report Bug</button>
              <button onClick={()=>{setQCRForm({project_id:id,priority:'Medium',request_type:'Change Request',title:'',description:''});setCrModal2(true);}}
                className="btn-secondary text-sm flex items-center gap-1.5"><PlusIc/> Submit CR</button>
            </div>
            {stages.length > 0 && !isLastStage && can('advance_stage') && (
              <button onClick={() => setAdvanceModal(true)}
                className="btn-primary text-sm flex items-center justify-center gap-1.5">
                Advance Stage <ChevronRight size={14}/>
              </button>
            )}
          </div>
        </div>

        {/* Progress */}
        <div className="mt-4">
          <div className="flex justify-between text-xs mb-1" style={{ color:'var(--text-secondary)' }}>
            <span>Task completion</span>
            <span className="font-semibold">{doneTasks}/{tasks.length} tasks · {progress}%</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background:'var(--border)' }}>
            <div className="h-full rounded-full bg-brand-500 transition-all" style={{ width:`${progress}%` }}/>
          </div>
        </div>
      </div>

      {/* RAG Health Scorecard */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm" style={{ color:'var(--text)' }}>Health Scorecard</h3>
          <div className="flex gap-2">
            {editingRAG
              ? <>
                  <button onClick={saveRAG} className="btn-primary text-xs px-3 py-1.5">Save</button>
                  <button onClick={() => setEditingRAG(false)} className="btn-secondary text-xs px-3 py-1.5">Cancel</button>
                </>
              : <button onClick={() => setEditingRAG(true)} className="btn-secondary text-xs px-3 py-1.5">Update RAG</button>
            }
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { key:'schedule', label:'Schedule' },
            { key:'budget',   label:'Budget'   },
            { key:'scope',    label:'Scope'    },
            { key:'risk',     label:'Risk'     },
          ].map(dim => (
            <RagBadge key={dim.key} label={dim.label}
              value={editingRAG ? ragValues[dim.key] : project['health_' + dim.key]}
              editable={editingRAG}
              onChange={v => setRagValues(r => ({ ...r, [dim.key]: v }))}
            />
          ))}
        </div>
      </div>

      {/* Effort rolled up from the project's activities. The API has returned
          these figures all along and nothing rendered them. */}
      {Number(project.act_count) > 0 && (() => {
        const est = parseFloat(project.act_estimated_hours) || 0;
        const act = parseFloat(project.act_actual_hours) || 0;
        const pct = est > 0 ? Math.min(100, Math.round((act / est) * 100)) : 0;
        const over = est > 0 && act > est;
        return (
          <div className="card py-3 px-4">
            <div className="flex items-center justify-between mb-2 text-sm">
              <span className="font-semibold" style={{ color:'var(--text)' }}>Effort</span>
              <span style={{ color:'var(--text-secondary)' }}>
                {act.toFixed(1)} / {est.toFixed(1)}h across {project.act_count} activit{Number(project.act_count)===1?'y':'ies'}
                {over && (
                  <span className="ml-2 px-1.5 py-0.5 rounded-full text-xs font-semibold"
                    style={{ background:'rgba(239,68,68,.12)', color:'#ef4444' }}>
                    +{(act - est).toFixed(1)}h over
                  </span>
                )}
              </span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background:'var(--border)' }}>
              <div className="h-full rounded-full transition-all"
                style={{ width:`${pct}%`, background: over ? '#ef4444' : '#5CC3BC' }}/>
            </div>
            {est === 0 && (
              <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
                No hours estimated yet, so there is nothing to track against.
              </p>
            )}
          </div>
        );
      })()}

      {/* Client info banner — shown only when project is linked to a client */}
      {project.client_id && (
        <div className="card flex items-center gap-4 py-3 px-4" style={{ border:'1px solid var(--border)' }}>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-white text-sm flex-shrink-0" style={{ background:'#2E539F' }}>
            {(project.company_name||project.first_name||'C')[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium" style={{ color:'var(--text-secondary)' }}>Client</p>
            <p className="font-semibold text-sm truncate" style={{ color:'var(--text)' }}>
              {project.company_name || `${project.first_name||''} ${project.last_name||''}`.trim()}
            </p>
          </div>
          <button onClick={() => navigate(`/clients/${project.client_id}`)}
            className="text-xs px-3 py-1.5 rounded-lg border hover:bg-[var(--surface-alt)] transition-colors flex-shrink-0 flex items-center gap-1.5"
            style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
            View Client
          </button>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatBox label="Total Tasks"  value={tasks.length} />
        <StatBox label="Completed"    value={doneTasks} />
        <StatBox label="Open Bugs"    value={openBugs}   color={critBugs ? '#ef4444' : undefined} warning={critBugs > 0} />
        <StatBox label="Pending CRs"  value={pendingCRs} color={pendingCRs > 0 ? '#FFC849' : undefined} />
        <StatBox label="Training"     value={`${(trainingHrs/60).toFixed(1)}h`} />
      </div>

      {/* Budget card — only when budget is set AND user can view financials */}
      {can('view_financials') && (project.budget_amount || project.baseline_budget) && (
        <div className="card">
          <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Budget</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Approved Budget</p>
              <p className="font-semibold mt-0.5" style={{ color:'var(--text)' }}>{fmtMoney(project.budget_amount, project.budget_currency)}</p>
            </div>
            <div>
              <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Actual Spend</p>
              <p className="font-semibold mt-0.5" style={{ color: budgetUsedPct > 90 ? '#ef4444' : 'var(--text)' }}>{fmtMoney(project.actual_spend, project.budget_currency)}</p>
            </div>
            <div>
              <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Baseline Budget</p>
              <p className="font-semibold mt-0.5" style={{ color:'var(--text)' }}>{fmtMoney(project.baseline_budget, project.budget_currency)}</p>
            </div>
            {budgetUsedPct !== null && (
              <div>
                <p className="text-xs mb-1" style={{ color:'var(--text-secondary)' }}>Budget Used</p>
                <div className="h-2 rounded-full overflow-hidden" style={{ background:'var(--border)' }}>
                  <div className="h-full rounded-full transition-all"
                    style={{ width:`${Math.min(budgetUsedPct,100)}%`, background: budgetUsedPct > 90 ? '#ef4444' : budgetUsedPct > 70 ? '#FFC849' : '#2E539F' }}/>
                </div>
                <p className="text-xs mt-1" style={{ color: budgetUsedPct > 90 ? '#ef4444' : 'var(--text-secondary)' }}>{budgetUsedPct}%</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b flex gap-1 overflow-x-auto" style={{ borderColor:'var(--border)' }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${activeTab === tab.key ? 'border-brand-500 text-brand-500' : 'border-transparent'}`}
            style={activeTab !== tab.key ? { color:'var(--text-secondary)' } : {}}>
            {tab.label}
            {tab.key==='tasks'    && tasks.length     > 0 && <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-brand-500/20 text-brand-500">{tasks.length}</span>}
            {tab.key==='bugs'     && bugs.length      > 0 && <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-500">{bugs.length}</span>}
            {tab.key==='changes'  && crs.length       > 0 && <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-500">{crs.length}</span>}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card">
            <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Task Breakdown</h3>
            {WORK_STATUSES.map(status => {
              const count = tasks.filter(t => t.status === status).length;
              const pct   = tasks.length ? Math.round((count/tasks.length)*100) : 0;
              return (
                <div key={status} className="flex items-center gap-3 mb-3">
                  <span className="text-xs w-24 flex-shrink-0" style={{ color:'var(--text-secondary)' }}>{status}</span>
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background:'var(--border)' }}>
                    <div className="h-full rounded-full bg-brand-500" style={{ width:`${pct}%` }}/>
                  </div>
                  <span className="text-xs w-6 text-right" style={{ color:'var(--text)' }}>{count}</span>
                </div>
              );
            })}
          </div>
          <div className="card">
            <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Team Members</h3>
            {(project.team_members||[]).length === 0
              ? <p className="text-sm" style={{ color:'var(--text-secondary)' }}>No members assigned yet</p>
              : (project.team_members||[]).map(m => (
                  <div key={m.id} className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white text-xs"
                      style={{ background: m.avatar_color||'#2E539F' }}>
                      {m.name?.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color:'var(--text)' }}>{m.name}</p>
                      {/* Show project role if assigned */}
                      {(() => {
                        const pr = projectRoles.find(r => r.user_id === m.id);
                        return pr ? <p className="text-xs" style={{ color:'#2E539F' }}>{pr.project_role}</p> : null;
                      })()}
                    </div>
                  </div>
                ))
            }
          </div>
        </div>
      )}

      {/* Tasks */}
      {/* Details — every field the API accepts, in one place. The modal this
          replaces offered 11 of 25; project type, go-live date, contract
          number, both maturity stages and every client contact field could be
          written by the API and reached from nowhere.
          The four health scores are NOT here: the RAG card above owns them,
          and two controls for one value is worse than none. */}
      {activeTab === 'details' && (
        <div className="space-y-4">
          <div className="card space-y-5">
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color:'#2E539F' }}>Project</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><label className="label">Name *</label>
                <input className="input" value={form.name||''} onChange={e=>set({name:e.target.value})}/></div>
              <div className="md:col-span-2"><label className="label">Description</label>
                <textarea className="input" rows={3} value={form.description||''} onChange={e=>set({description:e.target.value})}/></div>

              <div><label className="label">Status</label>
                <select className="select" value={form.status||''} onChange={e=>set({status:e.target.value})}>
                  {STATUS_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                </select></div>
              <div><label className="label">Priority</label>
                <select className="select" value={form.priority||''} onChange={e=>set({priority:e.target.value})}>
                  {PRIORITY_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                </select></div>

              <div><label className="label">Project type</label>
                <select className="select" value={form.project_type||''} onChange={e=>set({project_type:e.target.value})}>
                  <option value="">Not set</option>
                  {projectTypes.map(t=><option key={t.id||t.name} value={t.name}>{t.name}</option>)}
                </select></div>
              <div><label className="label">Onboarding stage</label>
                <select className="select" value={form.onboarding_stage||''} onChange={e=>set({onboarding_stage:e.target.value})}>
                  <option value="">Not set</option>
                  {ONBOARDING.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                </select></div>

              <div className="md:col-span-2"><label className="label">Client digital maturity</label>
                <select className="select" value={form.digital_maturity_stage||''} onChange={e=>set({digital_maturity_stage:e.target.value})}>
                  <option value="">Not assessed</option>
                  {MATURITY.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                </select></div>
            </div>
          </div>

          <div className="card space-y-5">
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color:'#2E539F' }}>Dates</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><label className="label">Start</label>
                <input type="date" className="input" value={(form.start_date||'').slice(0,10)} onChange={e=>set({start_date:e.target.value})}/></div>
              <div><label className="label">End</label>
                <input type="date" className="input" value={(form.end_date||'').slice(0,10)} onChange={e=>set({end_date:e.target.value})}/></div>
              <div><label className="label">Go live</label>
                <input type="date" className="input" value={(form.go_live_date||'').slice(0,10)} onChange={e=>set({go_live_date:e.target.value})}/></div>
            </div>
          </div>

          <div className="card space-y-5">
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color:'#2E539F' }}>Client and contract</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="label">Linked client record</label>
                <select className="select" value={form.client_id||''} onChange={e=>set({client_id:e.target.value})}>
                  <option value="">Not linked</option>
                  {clients.map(c=><option key={c.id} value={c.id}>{c.company_name || `${c.first_name||''} ${c.last_name||''}`.trim()}</option>)}
                </select></div>
              <div><label className="label">Client name</label>
                <input className="input" value={form.client||''} onChange={e=>set({client:e.target.value})}/>
                <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
                  Free text, for a client with no record yet.
                </p></div>
              <div><label className="label">Contact person</label>
                <input className="input" value={form.client_contact||''} onChange={e=>set({client_contact:e.target.value})}/></div>
              <div><label className="label">Contact email</label>
                <input type="email" className="input" value={form.client_email||''} onChange={e=>set({client_email:e.target.value})}/></div>
              <div><label className="label">Contact phone</label>
                <input className="input" value={form.client_phone||''} onChange={e=>set({client_phone:e.target.value})}/></div>
              <div><label className="label">Contract number</label>
                <input className="input" value={form.contract_number||''} onChange={e=>set({contract_number:e.target.value})}/></div>
            </div>
          </div>

          <div className="card space-y-5">
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color:'#2E539F' }}>Budget</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div><label className="label">Estimated value</label>
                <input type="number" step="0.01" className="input" value={form.estimated_value ?? ''} onChange={e=>set({estimated_value:e.target.value})}/></div>
              <div><label className="label">Budget allocated</label>
                <input type="number" step="0.01" className="input" value={form.budget_amount ?? ''} onChange={e=>set({budget_amount:e.target.value})}/></div>
              <div><label className="label">Budget actual</label>
                <input type="number" step="0.01" className="input" value={form.actual_spend ?? ''} onChange={e=>set({actual_spend:e.target.value})}/></div>
              <div><label className="label">Currency</label>
                <input className="input" value={form.budget_currency||''} onChange={e=>set({budget_currency:e.target.value})} placeholder="RWF"/></div>
            </div>
            <p className="text-xs" style={{ color:'var(--text-secondary)' }}>
              Leaving a budget field empty clears it. Health scores are set with
              Update RAG on the Overview tab.
            </p>
          </div>

          <div className="card">
            <CustomFieldsEditor appliesTo="project" projectTypeId={project?.project_type_id}
              values={form.custom_fields || {}}
              onChange={v => set({ custom_fields: v })} />
          </div>
        </div>
      )}

      <SaveBar dirtyFields={dirtyFields} discard={discard} onSave={handleSaveProject} saving={savingProj} />
      {activeTab === 'tasks' && (
        <div className="card overflow-hidden p-0">
          {tasks.length === 0
            ? <p className="text-center py-10 text-sm" style={{ color:'var(--text-secondary)' }}>
                No tasks yet — <Link to={`/tasks?project_id=${id}`} className="text-brand-500 hover:underline">add one</Link>
              </p>
            : <table className="w-full text-sm">
                <thead><tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                  {['Title','Status','Priority','Due Date','Assigned'].map(h=>
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {tasks.map(t => (
                    <tr key={t.id} className="border-b hover:bg-[var(--surface-alt)] cursor-pointer transition-colors"
                      style={{ borderColor:'var(--border)' }} onClick={() => navigate(`/tasks/${t.id}`)}>
                      <td className="px-4 py-3">
                        <p className="font-medium" style={{ color:'var(--text)' }}>{t.title}</p>
                        {t.work_type && <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{t.work_type}</p>}
                      </td>
                      <td className="px-4 py-3"><Badge value={t.status}   type="status"/></td>
                      <td className="px-4 py-3"><Badge value={t.priority} type="priority"/></td>
                      <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{fmt(t.due_date)}</td>
                      <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{t.assigned_to_name||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          }
        </div>
      )}

      {/* Bugs */}
      {activeTab === 'bugs' && (
        <div className="card overflow-hidden p-0">
          {bugs.length === 0
            ? <p className="text-center py-10 text-sm" style={{ color:'var(--text-secondary)' }}>
                No bugs — <Link to={`/bugs?project_id=${id}`} className="text-brand-500 hover:underline">report one</Link>
              </p>
            : <table className="w-full text-sm">
                <thead><tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                  {['Title','Severity','Status','Environment','Assigned','Reported'].map(h=>
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {bugs.map(b => (
                    <tr key={b.id} className="border-b hover:bg-[var(--surface-alt)] cursor-pointer transition-colors"
                      style={{ borderColor:'var(--border)' }} onClick={() => navigate(`/bugs/${b.id}`)}>
                      <td className="px-4 py-3 font-medium" style={{ color:'var(--text)' }}>{b.title}</td>
                      <td className="px-4 py-3"><Badge value={b.severity} type="severity"/></td>
                      <td className="px-4 py-3"><Badge value={b.status}   type="status"/></td>
                      <td className="px-4 py-3">{b.environment && <span className={`badge ${ENV_STYLE[b.environment]||''}`}>{b.environment}</span>}</td>
                      <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{b.assigned_to_name||'—'}</td>
                      <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{fmt(b.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          }
        </div>
      )}

      {/* Change Requests */}
      {activeTab === 'changes' && (
        <div className="card overflow-hidden p-0">
          {crs.length === 0
            ? <p className="text-center py-10 text-sm" style={{ color:'var(--text-secondary)' }}>No change requests</p>
            : <table className="w-full text-sm">
                <thead><tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                  {['Title','Impact','Status','Requested By','Target Date'].map(h=>
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {crs.map(c => (
                    <tr key={c.id} className="border-b hover:bg-[var(--surface-alt)] cursor-pointer transition-colors"
                      style={{ borderColor:'var(--border)' }} onClick={() => navigate(`/change-requests/${c.id}`)}>
                      <td className="px-4 py-3 font-medium" style={{ color:'var(--text)' }}>{c.title}</td>
                      <td className="px-4 py-3"><Badge value={c.impact_level}    type="priority"/></td>
                      <td className="px-4 py-3"><Badge value={c.approval_status} type="status"/></td>
                      <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{c.requested_by_name||'—'}</td>
                      <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{fmt(c.implementation_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          }
        </div>
      )}

      {/* Training */}
      {activeTab === 'training' && (
        <div className="card overflow-hidden p-0">
          {trainings.length === 0
            ? <p className="text-center py-10 text-sm" style={{ color:'var(--text-secondary)' }}>No training sessions</p>
            : <table className="w-full text-sm">
                <thead><tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                  {['Session','Date','Duration','Participants','Feedback'].map(h=>
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {trainings.map(t => (
                    <tr key={t.id} className="border-b transition-colors" style={{ borderColor:'var(--border)' }}>
                      <td className="px-4 py-3 font-medium" style={{ color:'var(--text)' }}>{t.session_name}</td>
                      <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{fmt(t.session_date)}</td>
                      <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{t.duration_minutes ? `${t.duration_minutes}m` : '—'}</td>
                      <td className="px-4 py-3 text-xs text-center" style={{ color:'var(--text-secondary)' }}>{t.participants?.length??0}</td>
                      <td className="px-4 py-3">
                        {t.feedback_score != null
                          ? <div className="flex items-center gap-2">
                              <div className="h-1.5 w-16 rounded-full overflow-hidden" style={{ background:'var(--border)' }}>
                                <div className="h-full rounded-full" style={{ width:`${(t.feedback_score/10)*100}%`, background: t.feedback_score>=8?'#10b981':t.feedback_score>=6?'#FFC849':'#ef4444' }}/>
                              </div>
                              <span className="text-xs font-semibold" style={{ color:'var(--text)' }}>{t.feedback_score}/10</span>
                            </div>
                          : <span className="text-xs" style={{ color:'var(--text-secondary)' }}>—</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          }
        </div>
      )}

      {/* Team & Roles */}
      {activeTab === 'team' && (
        <div className="space-y-5">
          {/* Role assignments table */}
          <div className="card overflow-hidden p-0">
            <div className="px-5 py-4 border-b" style={{ borderColor:'var(--border)' }}>
              <h3 className="font-semibold text-sm" style={{ color:'var(--text)' }}>Project Role Assignments</h3>
              <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>
                Project roles grant specific permissions on this project type independently of the system role.
                A Specialist can approve gates if assigned QA Lead here.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                    {['Team Member','System Role','Project Role','Effective Permissions on this Project','Assigned By',''].map(h=>
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {projectRoles.length === 0
                    ? <tr><td colSpan="6" className="text-center py-8 text-sm" style={{ color:'var(--text-secondary)' }}>
                        No project roles assigned yet — use the form below to assign roles to team members
                      </td></tr>
                    : projectRoles.map(r => (
                      <tr key={r.id} className="border-b hover:bg-[var(--surface-alt)] transition-colors" style={{ borderColor:'var(--border)' }}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-white text-xs flex-shrink-0" style={{ background:r.avatar_color||'#2E539F' }}>
                              {r.user_name?.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                            </div>
                            <span className="font-medium" style={{ color:'var(--text)' }}>{r.user_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{r.system_role}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background:'rgba(46,83,159,.1)', color:'#2E539F' }}>
                            {r.project_role}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(r.effective_permissions||[]).map(p => (
                              <span key={p} className="text-xs px-1.5 py-0.5 rounded"
                                style={{ background:'var(--surface-alt)', color:'var(--text-secondary)', border:'1px solid var(--border)' }}>
                                {p.replace(/_/g,' ')}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{r.assigned_by_name||'—'}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => removeRole(r.id)} className="text-xs text-red-400 hover:underline">Remove</button>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>

          {/* Assign role form — only for users with assign_roles permission */}
          {can('assign_roles') && <div className="card">
            <h3 className="font-semibold text-sm mb-1" style={{ color:'var(--text)' }}>Assign Project Role</h3>
            <p className="text-xs mb-4" style={{ color:'var(--text-secondary)' }}>
              The roles shown are filtered to those applicable to <strong>{project.project_type}</strong> projects.
              Only team members already on this project can be assigned a role.
            </p>
            <AssignRoleForm
              users={allUsers.filter(u => u.is_active)}
              roleDefs={roleDefs}
              projectMembers={(project.team_members||[]).map(m=>m.id)}
              onAssign={assignRole}
            />
          </div>}

          {/* Permission scheme reference */}
          <div className="card">
            <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>
              Permission Scheme for {project.project_type} Projects
            </h3>
            <p className="text-xs mb-4" style={{ color:'var(--text-secondary)' }}>
              This table shows what each role can do on a <strong>{project.project_type}</strong> project.
              Admins can modify these grants in the Admin Panel.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>Role</th>
                    {['advance_stage','approve_gates','lock_baseline','edit_budget','view_financials','manage_team','create_tasks','manage_risks'].map(p=>(
                      <th key={p} className="px-2 py-2 font-semibold uppercase tracking-wider text-center whitespace-nowrap" style={{ color:'var(--text-secondary)' }}>
                        {p.replace(/_/g,' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {roleDefs.map(role => (
                    <tr key={role.id} className="border-b hover:bg-[var(--surface-alt)]" style={{ borderColor:'var(--border)' }}>
                      <td className="px-3 py-2 font-medium" style={{ color:'var(--text)' }}>{role.name}</td>
                      {['advance_stage','approve_gates','lock_baseline','edit_budget','view_financials','manage_team','create_tasks','manage_risks'].map(pc=>(
                        <td key={pc} className="px-2 py-2 text-center">
                          {(role.permissions||[]).some(p=>p.permission_code===pc)
                            ? <span style={{ color:'#10b981' }}>✓</span>
                            : <span style={{ color:'var(--border)' }}>—</span>
                          }
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Workflow */}
      {activeTab === 'stages' && (
        <div className="space-y-4">
          <div className="card">
            <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>
              {project.project_type} Workflow
            </h3>
            {stages.length === 0
              ? <p className="text-sm" style={{ color:'var(--text-secondary)' }}>No workflow defined for this project type</p>
              : <StageTimeline stages={stages} currentStage={project.current_stage} />
            }
            {project.current_stage && (
              <div className="mt-4 p-3 rounded-xl" style={{ background:'var(--surface-alt)' }}>
                <p className="text-xs font-semibold" style={{ color:'var(--text-secondary)' }}>Current stage</p>
                <p className="font-semibold mt-0.5" style={{ color:'var(--text)' }}>{project.current_stage}</p>
                {stages.find(s => s.stage_name === project.current_stage)?.description && (
                  <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
                    {stages.find(s => s.stage_name === project.current_stage).description}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Stage history */}
          {stageHistory.length > 0 && (
            <div className="card overflow-hidden p-0">
              <div className="px-5 py-4 border-b" style={{ borderColor:'var(--border)' }}>
                <h3 className="font-semibold text-sm" style={{ color:'var(--text)' }}>Stage History</h3>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                  {['From','To','Changed By','Reason','Date'].map(h=>
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {stageHistory.map(h => (
                    <tr key={h.id} className="border-b" style={{ borderColor:'var(--border)' }}>
                      <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{h.from_stage||'—'}</td>
                      <td className="px-4 py-3 text-xs font-medium" style={{ color:'var(--text)' }}>{h.to_stage}</td>
                      <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{h.changed_by_name||'—'}</td>
                      <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{h.reason||'—'}</td>
                      <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{fmt(h.created_at,'MMM d, HH:mm')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Advance Stage Modal */}
      {advanceModal && (() => {
        const nextIdx = currentStageIdx + 1;
        const nextStage = nextIdx < stages.length ? stages[nextIdx] : null;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={() => setAdvanceModal(false)}/>
            <div className="relative w-full max-w-md rounded-2xl shadow-2xl p-6 z-10"
              style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
              <h3 className="font-bold mb-1" style={{ color:'var(--text)' }}>Advance to Next Stage</h3>
              {nextStage && (
                <p className="text-sm mb-4" style={{ color:'var(--text-secondary)' }}>
                  Moving from <strong>{project.current_stage||'(not started)'}</strong> to <strong style={{ color:'#2E539F' }}>{nextStage.stage_name}</strong>
                  {nextStage.requires_approval && <span className="ml-1 text-xs text-amber-600">(requires approval permission)</span>}
                </p>
              )}
              <div className="mb-3">
                <label className="label">Reason for advancing <span className="text-red-500">*</span></label>
                <textarea className="input" rows={3}
                  placeholder="What has been completed to justify moving to the next stage?"
                  value={advanceReason}
                  onChange={e => setAdvanceReason(e.target.value)}/>
              </div>
              <div className="mb-4">
                <label className="label">Assign this stage to <span className="text-xs font-normal" style={{ color:'var(--text-secondary)' }}>(optional)</span></label>
                <select className="select" value={advanceAssignTo} onChange={e => setAdvanceAssignTo(e.target.value)}>
                  <option value="">No specific assignee</option>
                  {allUsers.filter(u => u.is_active).map(u => (
                    <option key={u.id} value={u.id}>{u.name} — {u.role}</option>
                  ))}
                </select>
                <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
                  The selected user will be added to the project team for this stage.
                </p>
              </div>
              {advanceError && <p className="text-sm text-red-500 mb-3">{advanceError}</p>}
              <div className="flex justify-end gap-2">
                <button onClick={() => setAdvanceModal(false)} className="btn-secondary text-sm">Cancel</button>
                <button onClick={handleAdvance} disabled={advancing} className="btn-primary text-sm flex items-center gap-2">
                  {advancing && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                  Advance Stage
                </button>
              </div>
            </div>
          </div>
        );
      })()}


      {/* Quick Bug Report Modal */}
      {bugModal2 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={()=>setBugModal2(false)}/>
          <div className="relative bg-[var(--surface)] rounded-2xl shadow-2xl w-full max-w-lg z-10 max-h-[90vh] flex flex-col" style={{ border:'1px solid var(--border)' }}>
            <div className="px-6 py-4 border-b flex-shrink-0" style={{ borderColor:'var(--border)' }}>
              <h3 className="font-bold" style={{ color:'var(--text)' }}>Report Bug — {project?.name}</h3>
            </div>
            <div className="p-6 overflow-y-auto space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><label className="label">Title *</label>
                  <input className="input" value={qBugForm.title||''} onChange={e=>setQBugForm(f=>({...f,title:e.target.value}))} placeholder="Brief description of the bug"/></div>
                <div><label className="label">Issue Type</label>
                  <select className="select" value={qBugForm.issue_type||'Bug'} onChange={e=>setQBugForm(f=>({...f,issue_type:e.target.value}))}>
                    {['Bug','Request for Fix','Support','New Feature','REOPEN'].map(t=><option key={t} value={t}>{t}</option>)}
                  </select></div>
                <div><label className="label">Severity</label>
                  <select className="select" value={qBugForm.severity||'Minor'} onChange={e=>setQBugForm(f=>({...f,severity:e.target.value}))}>
                    {['Critical','Major','Minor'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select></div>
                <div><label className="label">Module</label>
                  <input className="input" value={qBugForm.module||''} onChange={e=>setQBugForm(f=>({...f,module:e.target.value}))} placeholder="e.g. Loans"/></div>
                <div><label className="label">Section</label>
                  <input className="input" value={qBugForm.section||''} onChange={e=>setQBugForm(f=>({...f,section:e.target.value}))} placeholder="e.g. Basic Info"/></div>
                <div className="col-span-2"><label className="label">Description *</label>
                  <textarea className="input" rows={3} value={qBugForm.description||''} onChange={e=>setQBugForm(f=>({...f,description:e.target.value}))} placeholder="Explain the issue..."/></div>
                <div className="col-span-2"><label className="label">Steps to Reproduce</label>
                  <textarea className="input" rows={2} value={qBugForm.steps_to_reproduce||''} onChange={e=>setQBugForm(f=>({...f,steps_to_reproduce:e.target.value}))} placeholder="1. Go to... 2. Click... 3. See error"/></div>
                <div><label className="label">Browser</label>
                  <input className="input" value={qBugForm.browser||''} onChange={e=>setQBugForm(f=>({...f,browser:e.target.value}))} placeholder="Chrome 120"/></div>
                <div><label className="label">URL Affected</label>
                  <input className="input" value={qBugForm.url_affected||''} onChange={e=>setQBugForm(f=>({...f,url_affected:e.target.value}))} placeholder="https://..."/></div>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2 flex-shrink-0" style={{ borderColor:'var(--border)' }}>
              <button onClick={()=>setBugModal2(false)} className="btn-secondary">Cancel</button>
              <button disabled={!qBugForm.title||!qBugForm.description||qSaving}
                onClick={async()=>{
                  setQSaving(true);
                  try { await bugsAPI.create(qBugForm);
                  toast.success('Bug created'); setBugModal2(false); }
                  catch(e) { toast.error(apiError(e, 'Error submitting bug')); }
                  finally { setQSaving(false); }
                }}
                className="btn-primary flex items-center gap-2">
                {qSaving&&<span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                Submit Bug Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick CR Modal */}
      {crModal2 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={()=>setCrModal2(false)}/>
          <div className="relative bg-[var(--surface)] rounded-2xl shadow-2xl w-full max-w-lg z-10 max-h-[90vh] flex flex-col" style={{ border:'1px solid var(--border)' }}>
            <div className="px-6 py-4 border-b flex-shrink-0" style={{ borderColor:'var(--border)' }}>
              <h3 className="font-bold" style={{ color:'var(--text)' }}>Submit CR — {project?.name}</h3>
            </div>
            <div className="p-6 overflow-y-auto space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><label className="label">Title *</label>
                  <input className="input" value={qCRForm.title||''} onChange={e=>setQCRForm(f=>({...f,title:e.target.value}))} placeholder="Brief title for this change request"/></div>
                <div><label className="label">Request Type</label>
                  <select className="select" value={qCRForm.request_type||'Change Request'} onChange={e=>setQCRForm(f=>({...f,request_type:e.target.value}))}>
                    {['Change Request','New Feature','Support','Enhancement'].map(t=><option key={t} value={t}>{t}</option>)}
                  </select></div>
                <div><label className="label">Priority</label>
                  <select className="select" value={qCRForm.priority||'Medium'} onChange={e=>setQCRForm(f=>({...f,priority:e.target.value}))}>
                    {['Critical','High','Medium','Low'].map(p=><option key={p} value={p}>{p}</option>)}
                  </select></div>
                <div><label className="label">Department</label>
                  <input className="input" value={qCRForm.department||''} onChange={e=>setQCRForm(f=>({...f,department:e.target.value}))} placeholder="e.g. Credit Department"/></div>
                <div><label className="label">Authorized Requestor</label>
                  <input className="input" value={qCRForm.authorized_requestor||''} onChange={e=>setQCRForm(f=>({...f,authorized_requestor:e.target.value}))} placeholder="Full name"/></div>
                <div className="col-span-2"><label className="label">Description *</label>
                  <textarea className="input" rows={4} value={qCRForm.description||''} onChange={e=>setQCRForm(f=>({...f,description:e.target.value}))} placeholder="Describe the change in detail..."/></div>
                <div className="col-span-2"><label className="label">Justification</label>
                  <textarea className="input" rows={2} value={qCRForm.justification||''} onChange={e=>setQCRForm(f=>({...f,justification:e.target.value}))} placeholder="Why is this change needed?"/></div>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2 flex-shrink-0" style={{ borderColor:'var(--border)' }}>
              <button onClick={()=>setCrModal2(false)} className="btn-secondary">Cancel</button>
              <button disabled={!qCRForm.title||!qCRForm.description||qSaving}
                onClick={async()=>{
                  setQSaving(true);
                  try { await changeRequestsAPI.create(qCRForm);
                  toast.success('Change request created'); setCrModal2(false); }
                  catch(e) { toast.error(apiError(e, 'Error submitting CR')); }
                  finally { setQSaving(false); }
                }}
                className="btn-primary flex items-center gap-2">
                {qSaving&&<span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                Submit Change Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Assign Role inline form ────────────────────────────────────────────────
function AssignRoleForm({ users, roleDefs, onAssign }) {
  // Its own component, so it needs its own hook — the page's `toast` below is
  // out of scope here. esbuild renaming the inner one to `toast2` is what
  // exposed this: a usage was still resolving to an outer binding that does
  // not exist.
  const toast = useToast();
  const [userId, setUserId] = React.useState('');
  const [roleId, setRoleId] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const handle = async () => {
    if (!userId || !roleId) return;
    setSaving(true);
    try { await onAssign(userId, roleId); setUserId(''); setRoleId(''); }
    catch(e) { toast.error(apiError(e, 'Could not assign role')); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <label className="label">Team Member</label>
        <select className="select" value={userId} onChange={e => setUserId(e.target.value)}>
          <option value="">Select member…</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      <div>
        <label className="label">Project Role</label>
        <select className="select" value={roleId} onChange={e => setRoleId(e.target.value)}>
          <option value="">Select role…</option>
          {roleDefs.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>
      <div className="flex items-end">
        <button onClick={handle} disabled={saving || !userId || !roleId}
          className="btn-primary text-sm w-full flex items-center justify-center gap-2">
          {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
          Assign Role
        </button>
      </div>
    </div>
  );
}
